import React from "react";
import { Box, SxProps, Theme } from "@mui/material";

export interface CustomSliderProps {
  /**
   * 現在のステップ（0から始まる）
   */
  currentStep: number;

  /**
   * スライド方向
   */
  direction: "next" | "prev";

  /**
   * アニメーション中かどうか
   */
  isAnimating: boolean;

  /**
   * 前のステップ（アニメーション用）
   */
  prevStep: number;

  /**
   * スライドするコンテンツ
   * 各ステップのコンテンツを配列で渡す
   */
  children: React.ReactNode[];

  /**
   * アニメーション時間（ミリ秒）
   * @default 500
   */
  animationDuration?: number;

  /**
   * コンテナの最小高さ
   */
  minHeight?: string | number;

  /**
   * カスタムスタイル
   */
  sx?: SxProps<Theme>;

  /**
   * カスタムクラス名
   */
  className?: string;

  /**
   * イージング関数
   * @default "cubic-bezier(0.4, 0.0, 0.2, 1)"
   */
  easing?: string;
}

/**
 * CustomSlider
 *
 * ステップ間をスライドアニメーションで遷移するコンポーネント
 *
 * @example
 * ```tsx
 * const [currentStep, setCurrentStep] = useState(0);
 * const [prevStep, setPrevStep] = useState(0);
 * const [isAnimating, setIsAnimating] = useState(false);
 * const [direction, setDirection] = useState<"next" | "prev">("next");
 *
 * const handleNext = () => {
 *   setPrevStep(currentStep);
 *   setDirection("next");
 *   setIsAnimating(true);
 *
 *   requestAnimationFrame(() => {
 *     requestAnimationFrame(() => {
 *       setCurrentStep(currentStep + 1);
 *       setTimeout(() => setIsAnimating(false), 500);
 *     });
 *   });
 * };
 *
 * <CustomSlider
 *   currentStep={currentStep}
 *   prevStep={prevStep}
 *   direction={direction}
 *   isAnimating={isAnimating}
 * >
 *   <div>Step 1 Content</div>
 *   <div>Step 2 Content</div>
 *   <div>Step 3 Content</div>
 * </CustomSlider>
 * ```
 */
const CustomSlider: React.FC<CustomSliderProps> = ({
  currentStep,
  direction,
  isAnimating,
  prevStep,
  children,
  animationDuration = 500,
  minHeight = "500px",
  sx,
  className = "",
  easing = "cubic-bezier(0.4, 0.0, 0.2, 1)",
}) => {
  const childrenArray = React.Children.toArray(children);

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight,
        ...sx,
      }}
      className={className}
    >
      {/* 前のステップ（スライドアウト） */}
      {isAnimating && childrenArray[prevStep] && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            willChange: "transform",
            animation:
              direction === "next"
                ? `slideOutLeft ${animationDuration}ms ${easing} forwards`
                : `slideOutRight ${animationDuration}ms ${easing} forwards`,
            "@keyframes slideOutLeft": {
              "0%": { transform: "translateX(0)" },
              "100%": { transform: "translateX(-100%)" },
            },
            "@keyframes slideOutRight": {
              "0%": { transform: "translateX(0)" },
              "100%": { transform: "translateX(100%)" },
            },
          }}
        >
          {childrenArray[prevStep]}
        </Box>
      )}

      {/* 現在のステップ（スライドイン） */}
      {childrenArray[currentStep] && (
        <Box
          key={currentStep}
          sx={{
            willChange: "transform",
            animation: isAnimating
              ? direction === "next"
                ? `slideInRight ${animationDuration}ms ${easing} forwards`
                : `slideInLeft ${animationDuration}ms ${easing} forwards`
              : "none",
            "@keyframes slideInRight": {
              "0%": { transform: "translateX(100%)" },
              "100%": { transform: "translateX(0)" },
            },
            "@keyframes slideInLeft": {
              "0%": { transform: "translateX(-100%)" },
              "100%": { transform: "translateX(0)" },
            },
          }}
        >
          {childrenArray[currentStep]}
        </Box>
      )}
    </Box>
  );
};

export default CustomSlider;
